
import { sigmaToPercentile } from '@/utils/personalityCalculations';
import { getProfileType } from '@/utils/profileTypeMapping';
import { calculateBehavioralScores } from '@/utils/scoreCalculations';

/**
 * Canonical profile type resolver — the single source of truth for profile classification.
 * 
 * This replicates the dashboard's logic (useBehavioralSubmissionData.ts):
 * 1. Detect M3 vs M1/M2 (via assessment_variant)
 * 2. Extract self-behavior scores for dual assessments, or top-level scores
 * 3. M3: use stored percentiles directly, threshold = 50
 *    M1/M2: convert z-scores to percentiles via sigmaToPercentile, threshold = 50
 * 4. Build DISC code, handle HHLL sub-categories
 * 5. Return getProfileType(discCode).type
 */
export const resolveProfileType = (submission: {
  scores?: any;
  assessment_variant?: string | null;
}): string => {
  if (!submission.scores) return 'Professional';

  const isM3Assessment = submission.assessment_variant === 'model3';
  const scores = calculateBehavioralScores(submission.scores);
  const percentileThreshold = 50;

  let dominancePercentile: number;
  let extraversionPercentile: number;
  let patiencePercentile: number;
  let formalityPercentile: number;

  // Raw values for HHLL sub-categorization tie-breaking
  let dominanceRaw: number;
  let extraversionRaw: number;
  let patienceRaw: number;
  let formalityRaw: number;

  if (isM3Assessment) {
    // M3: Use stored percentiles directly from selfBehavior or top-level
    const selfScores = submission.scores?.selfBehavior?.scores || submission.scores;

    dominancePercentile = selfScores?.dominancePercentile ?? 50;
    extraversionPercentile = selfScores?.extraversionPercentile ?? 50;
    patiencePercentile = selfScores?.patiencePercentile ?? 50;
    formalityPercentile = selfScores?.formalityPercentile ?? 50;

    dominanceRaw = dominancePercentile;
    extraversionRaw = extraversionPercentile;
    patienceRaw = patiencePercentile;
    formalityRaw = formalityPercentile;
  } else {
    // M1/M2: Calculate percentiles from z-scores
    const dominance = scores?.aScore || 0;
    const extraversion = scores?.bScore || 0;
    const patience = scores?.cScore || 0;
    const formality = scores?.dScore || 0;

    dominancePercentile = sigmaToPercentile(dominance);
    extraversionPercentile = sigmaToPercentile(extraversion);
    patiencePercentile = sigmaToPercentile(patience);
    formalityPercentile = sigmaToPercentile(formality);

    // For HHLL tie-breaking, use raw z-scores (same as dashboard)
    dominanceRaw = dominance;
    extraversionRaw = extraversion;
    patienceRaw = patience;
    formalityRaw = formality;
  }

  const d = dominancePercentile >= percentileThreshold ? 'H' : 'L';
  const i = extraversionPercentile >= percentileThreshold ? 'H' : 'L';
  const s = patiencePercentile >= percentileThreshold ? 'H' : 'L';
  const c = formalityPercentile >= percentileThreshold ? 'H' : 'L';

  let profileKey = `${d}${i}${s}${c}`;

  if (profileKey === 'HHLL') {
    const highestIsDominance = dominanceRaw >= extraversionRaw;
    const structureOverPatience = formalityRaw > patienceRaw;

    if (highestIsDominance) {
      profileKey = structureOverPatience ? 'HHLL-AHDH' : 'HHLL-AHDL';
    } else {
      profileKey = structureOverPatience ? 'HHLL-BHDH' : 'HHLL-BHDL';
    }
  }

  const profileTypeData = getProfileType(profileKey);
  return profileTypeData?.type || 'Professional';
};
